import { CheckRevenuesPeriodFacts } from './check-revenues-facts.model';
import { CurrencyPipe } from '@angular/common';
import { TimeGranularityEnum } from '../../shared/services/data-services/graphql';
import moment from 'moment';

export class CheckRevenuesDataSeries {
  _id: string;
  feName: string;
  dataSeries: Array<CheckRevenuesPeriodFacts> = [];
  isGreen: any;
  code?: string;
  country: any;
  static mapFromPark(park: any, granularity: TimeGranularityEnum) {
    if (park) {
      park.energyMeterDataserie = park.energyMeterDataseries;
      let series: Array<CheckRevenuesPeriodFacts> = [];
      switch (granularity) {
        case TimeGranularityEnum.Hour: series = CheckRevenuesDataSeries.fillMissingParkDataHour(park); break;
        case TimeGranularityEnum.Day: series = CheckRevenuesDataSeries.fillMissingParkDataDay(park); break;
        case TimeGranularityEnum.Month: series = CheckRevenuesDataSeries.fillMissingParkDataMonth(park); break;
      }

      return new CheckRevenuesDataSeries({
        country: park.country,
        isGreen: park.isGreen,
        feName: park.fe_name, dataSeries: series
      });
    } else {
      return null;
    }
  }

  static mapFromPortfolio(parks: any, dataSeries: any, granularity: TimeGranularityEnum, isComparison = false) {
    const mappedParks: Array<CheckRevenuesDataSeries> = [];

    parks.forEach((park) => {
      if (park) {
        park.energyMeterDataserie = !isComparison ? dataSeries : park.energyMeterDataseries;
        let series: Array<CheckRevenuesPeriodFacts> = [];
        switch (granularity) {
          case TimeGranularityEnum.Hour: series = CheckRevenuesDataSeries.fillMissingParkDataHour(park); break;
          case TimeGranularityEnum.Day: series = CheckRevenuesDataSeries.fillMissingParkDataDay(park); break;
          case TimeGranularityEnum.Month: series = CheckRevenuesDataSeries.fillMissingParkDataMonth(park); break;
        }
        const p = new CheckRevenuesDataSeries({ isGreen: park.isGreen, _id: park._id, feName: park.fe_name, code: park.code, dataSeries: (isComparison && park.energyMeterDataseries.length) ? park.energyMeterDataseries : series });
        mappedParks.push(p);
      } else {
        return null;
      }
    });

    return mappedParks;
  }

  static fillMissingParkDataHour(park: any) {
    // in some cases we don't get the whole month from the backend
    // -> we need to fill up the missing days
    let series: Array<CheckRevenuesPeriodFacts> = [];
    if (park.energyMeterDataserie.length > 0) {
      const startOfResultDay = moment(park.energyMeterDataserie[0].period).startOf('day');
      const energyMeterCloned = [...park.energyMeterDataserie];
      const currentHour = startOfResultDay.clone();
      while (currentHour.day() === startOfResultDay.day() && currentHour <= moment()) {
        const index =  energyMeterCloned.findIndex(elem => (elem.period === currentHour.format('YYYY-MM-DD HH:mm:ss')));
        const hourDataAlreadyPresent = energyMeterCloned[index];
        if (!hourDataAlreadyPresent) {
          const newFactElement = this.createEmptyFactsElement(currentHour.format('YYYY-MM-DD HH:mm:ss'));
          series.push(newFactElement);
        }
        else {
          const newFactElement = this.createFactsElementFromBackendData(park, hourDataAlreadyPresent);
          energyMeterCloned.splice(index, 1)
          series.push(newFactElement);
        }
        currentHour.add(1, 'hour');
      }
    }
    return series;
  }

  static fillMissingParkDataDay(park: any) {
    // in some cases we don't get the whole month from the backend
    // -> we need to fill up the missing days
    let series: Array<CheckRevenuesPeriodFacts> = [];
    if (park.energyMeterDataserie.length > 0) {
      const startOfResultMonth = moment(park.energyMeterDataserie[0].period).startOf('month');

      const currentDay = startOfResultMonth.clone();
      while (currentDay.month() === startOfResultMonth.month() && currentDay <= moment()) {
        const dayDataAlreadyPresent = park.energyMeterDataserie.find(elem => (elem.period === currentDay.format('YYYY-MM-DD')))
        if (!dayDataAlreadyPresent) {
          const newFactElement = this.createEmptyFactsElement(currentDay.format('YYYY-MM-DD'));
          series.push(newFactElement);
        }
        else {
          const newFactElement = this.createFactsElementFromBackendData(park, dayDataAlreadyPresent);
          series.push(newFactElement);
        }
        currentDay.add(1, 'day');
      }
    }
    return series;
  }

  static fillMissingParkDataMonth(park: any) {
    // in some cases we don't get the whole month from the backend
    // -> we need to fill up the missing days
    let series: Array<CheckRevenuesPeriodFacts> = [];
    if (park.energyMeterDataserie.length > 0) {
      const startOfResultYear = moment(park.energyMeterDataserie[0].period).startOf('year');

      const currentMonth = startOfResultYear.clone();
      while (currentMonth.year() === startOfResultYear.year() && currentMonth <= moment()) {
        const monthDataAlreadyPresent = park.energyMeterDataserie.find(elem => (elem.period === currentMonth.format('YYYY-MM')))
        if (!monthDataAlreadyPresent) {
          const newFactElement = this.createEmptyFactsElement(currentMonth.format('YYYY-MM'));
          series.push(newFactElement);
        }
        else {
          const newFactElement = this.createFactsElementFromBackendData(park, monthDataAlreadyPresent);
          series.push(newFactElement);
        }
        currentMonth.add(1, 'month');
      }
    } else {
      const newFactElement = this.createEmptyFactsElement(moment().format('YYYY-MM'));
      series.push(newFactElement);
    }
    return series;
  }

  private static createEmptyFactsElement(period: string): CheckRevenuesPeriodFacts {
    return new CheckRevenuesPeriodFacts({
      period: period,
      productionScada: 0,
      productionGrid: 0,
      productionGridCount: 0,
      productionLosses: 0,
      marketPrice: 0,
      referencePrice: 0,
      referencePriceRevenues: 0,
      tariff: 0,
      scadaRevenue: 0,
      totalRevenue: 0,
      revenueLosses: 0,
      consumptionLosses: 0,
      electricitySalesRevenue: 0,
      incentiveRevenue: 0,
      consumptionGrid: 0,
      PPAElectricityPrice: 0,
      incentive: 0,
      totalTariff: 0,
      realizedPPA: 0,
      PPAElectricityPriceByPark: 0,
      marketPriceRevenues: 0,
      ppaPrice: 0,
      ppaRevenue: 0,
			riskPremium: 0
    });

  }

  private static createFactsElementFromBackendData(park, dataSeriesElement) {
    return new CheckRevenuesPeriodFacts({
      feName: '',
      period: dataSeriesElement.period,
      productionScada: dataSeriesElement.productionScada,
      productionGrid: dataSeriesElement.productionGrid,
      productionGridCount: dataSeriesElement.productionGridCount,
      productionLosses: Math.max(dataSeriesElement.productionLosses, 0),
      marketPrice: dataSeriesElement.marketPrice,
      referencePrice: dataSeriesElement.referencePrice,
      referencePriceRevenues: dataSeriesElement.referencePriceRevenues,
      tariff: dataSeriesElement.tariff,
      scadaRevenue: dataSeriesElement.scadaRevenue,
      totalRevenue: dataSeriesElement.totalRevenue,
      revenueLosses: dataSeriesElement.revenueLosses,
      consumptionLosses: dataSeriesElement.consumptionLosses,
      electricitySalesRevenue: park.fe_name !== 'Juvent' ? dataSeriesElement.electricitySalesRevenue : 0,
      incentiveRevenue: dataSeriesElement.incentiveRevenue,
      consumptionGrid: dataSeriesElement.consumptionGrid,
      PPAElectricityPrice: dataSeriesElement.PPAElectricityPrice,
      incentive: dataSeriesElement.incentive,
      totalTariff: dataSeriesElement.totalTariff,
      realizedPPA: dataSeriesElement.realizedPPA,
      PPAElectricityPriceByPark: dataSeriesElement.electricitySalesRevenue / dataSeriesElement.productionGrid,
      marketPriceRevenues: dataSeriesElement.marketPriceRevenues,
      neasFee: dataSeriesElement.neasFee,
      statnettFee: dataSeriesElement.statnettFee,
      estimatedBalancingFee: dataSeriesElement.estimatedBalancingFee,
      elcertsHedgePrice: dataSeriesElement.elcertsHedgePrice,
      hedgedPpaCapacity: dataSeriesElement.hedgedPpaCapacity,
      monthlyPpaVolume: dataSeriesElement.monthlyPpaVolume,
      financialHedge: dataSeriesElement.financialHedge,
      powerHedgePrice: dataSeriesElement.powerHedgePrice,
      ppaPrice: dataSeriesElement.ppaPrice,
      ppaRevenue: dataSeriesElement.ppaRevenue,
			riskPremium: dataSeriesElement.riskPremium
    });
  }

  static createNewSeries() {
    const series: Array<CheckRevenuesPeriodFacts> = [];
    const emptyData = new CheckRevenuesPeriodFacts({
      _id: null,
      feName: '',
      period: '',
      productionScada: 0,
      productionGrid: 0,
      productionGridCount: 0,
      productionLosses: 0,
      marketPrice: 0,
      tariff: 0,
      scadaRevenue: 0,
      totalRevenue: 0,
      revenueLosses: 0,
      consumptionLosses: 0,
      electricitySalesRevenue: 0,
      incentiveRevenue: 0,
      consumptionGrid: 0,
      PPAElectricityPrice: 0,
      incentive: 0,
      totalTariff: 0,
      realizedPPA: 0,
      PPAElectricityPriceByPark: 0,
      marketPriceRevenues: 0,
      referencePriceRevenues: 0,
      neasFee: 0,
      statnettFee: 0,
      estimatedBalancingFee: 0,
      elcertsHedgePrice: 0,
      hedgedPpaCapacity: 0,
      monthlyPpaVolume: 0,
      financialHedge: 0,
      powerHedgePrice: 0,
      ppaPrice: 0,
      ppaRevenue: 0,
			riskPremium: 0
    });
    series.push(emptyData);
    return new CheckRevenuesDataSeries({ isGreen: false, feName: '', dataSeries: series });
  }

  constructor(init: Partial<CheckRevenuesDataSeries>) {
    Object.assign(this, init);
  }
}
